import {useState, useEffect, useRef} from 'react';
import {Form, Upload, Button as AntButton, Input} from 'antd';
import {DeleteOutlined, UploadOutlined, EyeOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import {useMutation} from '@apollo/client';
import styles from './fileUploadList.module.scss';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {fullyDecodeURL} from 'utils/helper';
import {UPDATE_WALLET_TRANSACTION_FILES} from 'graphql/mutations/wallet';

const FileUploadList = ({
  form,
  name = 'files',
  label = 'Files',
  bucket = 'campaign-bucket-images',
  initialFiles = null,
  transactionId = null,
  forType = null, // 'SWAP' or 'WALLET'
  currency = null,
  refetch = null,
}) => {
  const [uploading, setUploading] = useState(false);
  const [presignMediaURL] = useMutation(PRE_SIGN_MEDIA_URL);
  const [fileName, setFileName] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const filesRef = useRef([]);
  const hasInitialized = useRef(false);

  const [modifyFilesForTransaction, {loading: loadingFilesMutation}] =
    useMutation(UPDATE_WALLET_TRANSACTION_FILES, {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    });

  // Watch the files array to update display
  const filesValue = Form.useWatch(name, form);
  const files = Array.isArray(filesValue) ? filesValue : [];

  // Initialize form field with initialFiles if provided, otherwise empty array
  useEffect(() => {
    if (!hasInitialized.current) {
      const currentValue = form.getFieldValue(name);
      if (
        initialFiles &&
        Array.isArray(initialFiles) &&
        initialFiles.length > 0
      ) {
        // Only set if form field is empty or not set
        if (
          !currentValue ||
          !Array.isArray(currentValue) ||
          currentValue.length === 0
        ) {
          form.setFieldsValue({[name]: initialFiles});
          filesRef.current = initialFiles;
        }
      } else if (!Array.isArray(currentValue)) {
        form.setFieldsValue({[name]: []});
      }
      hasInitialized.current = true;
    }
  }, [form, name, initialFiles]);

  // Keep a ref of the files to prevent clearing
  useEffect(() => {
    if (Array.isArray(filesValue)) {
      filesRef.current = filesValue;
    } else {
      filesRef.current = [];
    }
  }, [filesValue]);

  const validateFileType = file => {
    const isValidType =
      file.type.startsWith('image/') ||
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.endsWith('.pdf') ||
      file.name.endsWith('.doc') ||
      file.name.endsWith('.docx');
    return isValidType;
  };

  const handleFileUpload = async file => {
    if (!validateFileType(file)) {
      toast.error('Only images, PDFs, and Word documents are allowed');
      return false;
    }

    if (!fileName.trim()) {
      toast.error('Please enter a file name');
      return false;
    }

    setUploading(true);

    try {
      const url = await UploadDocumentToBucket(file, () => {}, bucket, false);
      if (url) {
        setPendingFile({
          name: fileName.trim(),
          url: url,
          type: file.type || '',
        });
        toast.success('File uploaded successfully. Click Save to add it.');
        return true;
      } else {
        toast.error('File upload failed');
        return false;
      }
    } catch (error) {
      toast.error('File upload failed');
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (!pendingFile) return;

    if (!fileName.trim()) {
      toast.error('Please enter a file name');
      return;
    }

    // Use ref to ensure we have the latest files, fallback to form value
    const currentFiles =
      filesRef.current.length > 0
        ? filesRef.current
        : Array.isArray(form.getFieldValue(name))
        ? form.getFieldValue(name)
        : [];

    const newFile = {
      ...pendingFile,
      name: fileName.trim(),
    };
    const updatedFiles = Array.isArray(currentFiles)
      ? [...currentFiles, newFile]
      : [newFile];

    // Only call mutation if BOTH transactionId AND forType are provided
    // Otherwise, just update form fields (works for modals and other use cases)
    if (transactionId && forType) {
      setSaving(true);
      modifyFilesForTransaction({
        variables: {
          input: {
            id: transactionId,
            for: forType,
            files: updatedFiles,
            ...(currency && {currency}),
          },
        },
      })
        .then(result => {
          const response = result?.data?.modifyFilesForTransaction;
          if (response?.__typename === 'Error') {
            toast.error(response.message);
            return;
          }

          filesRef.current = updatedFiles;
          form.setFieldsValue({[name]: updatedFiles});
          setPendingFile(null);
          setFileName('');
          toast.success('File saved successfully');
          if (refetch) {
            refetch();
          }
        })
        .catch(error => {
          toast.error(error?.message || 'Failed to save file');
        })
        .finally(() => {
          setSaving(false);
        });
    } else {
      // Update form fields only (no mutation call)
      // This works for modals and cases where transactionId is provided but forType is not
      filesRef.current = updatedFiles;
      form.setFieldsValue({[name]: updatedFiles});
      setPendingFile(null);
      setFileName('');
      toast.success('File saved successfully');
    }
  };

  const handleCancel = () => {
    setPendingFile(null);
    setFileName('');
  };

  const handleDelete = index => {
    // Use ref to ensure we have the latest files, fallback to form value
    const currentFiles =
      filesRef.current.length > 0
        ? filesRef.current
        : Array.isArray(form.getFieldValue(name))
        ? form.getFieldValue(name)
        : [];
    const newFiles = Array.isArray(currentFiles)
      ? currentFiles.filter((_, i) => i !== index)
      : [];

    // Only call mutation if BOTH transactionId AND forType are provided
    // Otherwise, just update form fields (works for modals and other use cases)
    if (transactionId && forType) {
      setSaving(true);
      modifyFilesForTransaction({
        variables: {
          input: {
            id: transactionId,
            for: forType,
            files: newFiles,
            ...(currency && {currency}),
          },
        },
      })
        .then(result => {
          const response = result?.data?.modifyFilesForTransaction;
          if (response?.__typename === 'Error') {
            toast.error(response.message);
            return;
          }

          filesRef.current = newFiles;
          form.setFieldsValue({[name]: newFiles});
          toast.success('File deleted successfully');
          if (refetch) {
            refetch();
          }
        })
        .catch(error => {
          toast.error(error?.message || 'Failed to delete file');
        })
        .finally(() => {
          setSaving(false);
        });
    } else {
      // Update form fields only (no mutation call)
      // This works for modals and cases where transactionId is provided but forType is not
      filesRef.current = newFiles;
      form.setFieldsValue({[name]: newFiles});
    }
  };

  const handleView = file => {
    if (file.url && file.url.includes('amazonaws.com')) {
      const bucket = file.url.split('.s3')[0].replace('https://', '');
      const filename = file.url.split('amazonaws.com/')[1];

      presignMediaURL({
        variables: {
          input: {
            filename: fullyDecodeURL(filename),
            bucket,
          },
        },
      })
        .then(({data: {presignMediaURL}}) => {
          if (presignMediaURL.__typename === 'Error') {
            toast.error(presignMediaURL.message);
          } else {
            window.open(
              presignMediaURL?.data?.url,
              '_blank',
              'noopener,noreferrer',
            );
          }
        })
        .catch(error =>
          // Was `toast.error('…', error)`: the second argument is toast's
          // options object, so the reason never reached the operator.
          toast.error(error?.message || 'Something went wrong'),
        );
    } else {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Use ref value if form value is empty but ref has files (prevent clearing)
  const displayFiles =
    files.length > 0
      ? files
      : filesRef.current.length > 0
      ? filesRef.current
      : [];

  return (
    <div className={styles.container}>
      {/* Hidden form field to store the files array */}
      <Form.Item name={name} hidden>
        <Input type="hidden" />
      </Form.Item>

      {/* Display label */}
      <div className={styles.label}>{label}</div>

      {/* Upload UI - separate from form field */}
      <div className={styles.uploadContainer}>
        {displayFiles.length > 0 && (
          <div className={styles.filesList}>
            {displayFiles.map((file, index) => (
              <div key={index} className={styles.fileItem}>
                <div className={styles.fileInfo}>
                  <span className={styles.fileName}>
                    {file.name || `File ${index + 1}`}
                  </span>
                  {file.type && (
                    <span className={styles.fileType}>
                      {file.type.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className={styles.fileActions}>
                  <AntButton
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(file)}
                    className={styles.viewButton}>
                    View
                  </AntButton>
                  <AntButton
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(index)}
                    loading={saving || loadingFilesMutation}
                    disabled={saving || loadingFilesMutation}
                    className={styles.deleteButton}>
                    Delete
                  </AntButton>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={styles.uploadForm}>
          <Input
            placeholder="File name"
            value={fileName}
            onChange={e => setFileName(e.target.value)}
            className={styles.nameInput}
            disabled={!!pendingFile}
          />
          {!pendingFile ? (
            <Upload
              accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              maxCount={1}
              beforeUpload={file => {
                if (!validateFileType(file)) {
                  toast.error(
                    'Only images, PDFs, and Word documents are allowed',
                  );
                  return Upload.LIST_IGNORE;
                }
                handleFileUpload(file);
                return false; // Prevent auto upload
              }}
              showUploadList={false}>
              <AntButton
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={uploading || !fileName.trim()}>
                Upload File
              </AntButton>
            </Upload>
          ) : (
            <div className={styles.saveActions}>
              <AntButton
                onClick={handleCancel}
                disabled={saving || loadingFilesMutation}>
                Cancel
              </AntButton>
              <AntButton
                type="primary"
                onClick={handleSave}
                loading={saving || loadingFilesMutation}>
                Save
              </AntButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUploadList;
